/** Parse and route member-invite join URLs. Token verify is same-origin (invited slug). */

export type InviteJoinQuery = {
  invite: boolean;
  email: string;
  token: string;
  slug: string;
};

export type InviteJoinAction =
  | { type: 'redirect'; href: string }
  | { type: 'consume'; email: string; token: string }
  | { type: 'otp'; email: string }
  | { type: 'none' };

export function parseInviteJoinQuery(search: string): InviteJoinQuery {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const email = (q.get('email') || '').trim();
  const token = (q.get('token') || '').trim().toLowerCase();
  const slug = (q.get('slug') || '').trim().toLowerCase();
  return {
    invite: q.get('invite') === '1',
    email,
    token,
    slug,
  };
}

export function slugFromCommunioHost(hostname: string, baseDomain = 'communiosocial.com'): string {
  const host = hostname.trim().toLowerCase().replace(/\.$/, '');
  const base = baseDomain.trim().toLowerCase().replace(/^\./, '');
  const suffix = `.${base}`;
  if (!host.endsWith(suffix)) return '';
  const slug = host.slice(0, -suffix.length);
  if (!slug || slug.includes('.') || slug === 'www' || slug === 'ops') return '';
  return slug;
}

/**
 * If the invite names a community slug and this page is not that host, return the
 * invited origin + same query. Never consume a token on the last-visited community.
 */
export function inviteJoinRedirectHref(opts: {
  search: string;
  hostname: string;
  protocol?: string;
  baseDomain?: string;
}): string | null {
  const parsed = parseInviteJoinQuery(opts.search);
  if (!parsed.invite || !parsed.slug) return null;
  const base = (opts.baseDomain || 'communiosocial.com').trim().toLowerCase();
  const pageSlug = slugFromCommunioHost(opts.hostname, base);
  if (pageSlug === parsed.slug) return null;
  const proto = opts.protocol === 'http:' ? 'http:' : 'https:';
  const q = new URLSearchParams();
  q.set('invite', '1');
  if (parsed.email) q.set('email', parsed.email);
  if (parsed.token) q.set('token', parsed.token);
  q.set('slug', parsed.slug);
  return `${proto}//${parsed.slug}.${base}/?${q.toString()}`;
}

export function inviteJoinAction(opts: {
  search: string;
  hostname: string;
  protocol?: string;
  baseDomain?: string;
}): InviteJoinAction {
  const parsed = parseInviteJoinQuery(opts.search);
  if (!parsed.invite) return { type: 'none' };
  const redirect = inviteJoinRedirectHref(opts);
  if (redirect) return { type: 'redirect', href: redirect };
  if (parsed.token && parsed.email.includes('@')) {
    return { type: 'consume', email: parsed.email, token: parsed.token };
  }
  if (parsed.email.includes('@')) {
    return { type: 'otp', email: parsed.email };
  }
  return { type: 'none' };
}

/** Drop invite consume params after the token is used (or no longer needed). */
export function strippedInviteHref(href: string): string {
  const url = new URL(href);
  url.searchParams.delete('invite');
  url.searchParams.delete('email');
  url.searchParams.delete('token');
  url.searchParams.delete('slug');
  const q = url.searchParams.toString();
  return `${url.pathname}${q ? `?${q}` : ''}${url.hash}`;
}
